/**
 * Cloud implementation of the shared DiscoveryApi. Node-scoped. Cloud has no live stream,
 * so watchScan polls the node-scoped results endpoint until the count stabilises (or a cap).
 */
import type { DiscoveryApi, DiscoveryResults } from '@trinetra/shared';
import { startDiscovery, getDiscoveryResults, bulkApproveDiscovery, getNodeCameras } from '../services/api.service';
import { selectedNodeId as nodeId } from './node';

const PAGE_SIZE = 50;

function normalize(res: any): DiscoveryResults {
  if (res?.pagination) return { data: res.data || [], total: res.pagination.total };
  const arr = Array.isArray(res) ? res : res?.data || [];
  return { data: arr, total: arr.length };
}

function ipsFrom(cameras: any[]): string[] {
  const ips = new Set<string>();
  for (const cam of cameras) {
    if (cam.ip_address) ips.add(cam.ip_address);
    else if (cam.rtsp_url) {
      const m = cam.rtsp_url.match(/@([\d.]+)[:/]/);
      if (m) ips.add(m[1]);
    }
  }
  return [...ips];
}

export const cloudDiscoveryApi: DiscoveryApi = {
  async startScan(subnet) {
    await startDiscovery(nodeId() as string, subnet);
  },
  getResults: (params) => getDiscoveryResults(nodeId() as string, params).then(normalize),
  bulkApprove: (cameras) => bulkApproveDiscovery(nodeId() as string, cameras),
  async listExistingCameraIps() {
    const id = nodeId();
    if (!id) return [];
    try {
      const existing: any = await getNodeCameras(id);
      return ipsFrom(Array.isArray(existing) ? existing : existing?.data || []);
    } catch {
      return [];
    }
  },
  watchScan({ onResults, onDone }) {
    const id = nodeId();
    if (!id) { onDone('No node selected'); return () => {}; }
    let attempts = 0, last = 0, stable = 0;
    let interval: ReturnType<typeof setInterval> | null = null;
    const startTimer = setTimeout(poll, 1500);
    function stop() { clearTimeout(startTimer); if (interval) clearInterval(interval); interval = null; }
    async function poll() {
      attempts++;
      try {
        const { data, total } = normalize(await getDiscoveryResults(id as string, { page: 1, limit: PAGE_SIZE }));
        onResults(data, total);
        if (total === last && total > 0) stable++; else stable = 0;
        last = total;
        if ((stable >= 4 && last > 0) || attempts >= 60) { stop(); onDone(); }
      } catch {
        if (attempts >= 60) { stop(); onDone(); }
      }
    }
    interval = setInterval(poll, 2000);
    return stop;
  },
};
